import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * Badge do JusZap no menu lateral.
 *
 * O backend do JusZap não tem contador de mensagens não lidas nem emite
 * notificações pro inbox do jusfy-backend, então o badge é derivado do que
 * existe em `state.app.juszap_configuration` (GET /juszap-configuration):
 *
 * - Configuração pendente de conexão (WhatsApp desconectado ou instância
 *   aguardando upgrade) → badge fixo de 1, sinalizando ação necessária
 *   (escanear QR code).
 * - Conectado → `conversationMeta.all_count`: conversas com status OPEN, que
 *   no JusZap significam atendimento humano em andamento/esperando (o robô
 *   opera nas PENDING).
 * - Cancelado ou sem configuração → sem badge.
 */

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

type JusZapConfigurationState = {
  hasConfiguration?: boolean;
  configuration?: {
    status?: string;
    whatsapp_connected?: boolean;
  } | null;
  conversationMeta?: {
    mine_count?: number;
    unassigned_count?: number;
    all_count?: number;
  } | null;
} | null;

type JusZapStateSlice = {
  app?: {
    juszap_configuration?: JusZapConfigurationState;
    is_juszap_configuration_loading?: boolean;
  };
  auth?: {
    authToken?: string | null;
  };
};

export const resolveJusZapMenuBadgeCount = (
  juszapConfiguration: JusZapConfigurationState,
): number => {
  if (!juszapConfiguration?.hasConfiguration) return 0;

  const configuration = juszapConfiguration.configuration;
  if (!configuration || configuration.status === 'CANCELLED') return 0;

  const needsConnection =
    configuration.status === 'UPDATE_REQUIRED' ||
    configuration.whatsapp_connected === false;
  if (needsConnection) return 1;

  return Math.max(0, juszapConfiguration.conversationMeta?.all_count ?? 0);
};

export function useJusZapMenuBadge(): number {
  const dispatch = useDispatch();
  const authToken = useSelector(
    (state: JusZapStateSlice) => state.auth?.authToken ?? null,
  );
  const juszapConfiguration = useSelector(
    (state: JusZapStateSlice) => state.app?.juszap_configuration ?? null,
  );
  const isLoading = useSelector((state: JusZapStateSlice) =>
    Boolean(state.app?.is_juszap_configuration_loading),
  );

  const initialLoadIssuedRef = useRef(false);

  // Carga inicial: o dado persistido em localStorage pode estar velho, então
  // atualiza uma vez por sessão assim que o menu monta
  useEffect(() => {
    if (!authToken || isLoading || initialLoadIssuedRef.current) return;
    initialLoadIssuedRef.current = true;
    dispatch({ type: 'LOAD_JUSZAP_CONFIGURATION', payload: { authToken } });
  }, [authToken, isLoading, dispatch]);

  // Refresh periódico só pra quem realmente tem JusZap
  const hasConfiguration = juszapConfiguration?.hasConfiguration === true;
  useEffect(() => {
    if (!authToken || !hasConfiguration) return undefined;

    const intervalId = setInterval(() => {
      dispatch({ type: 'LOAD_JUSZAP_CONFIGURATION', payload: { authToken } });
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [authToken, hasConfiguration, dispatch]);

  return useMemo(
    () => resolveJusZapMenuBadgeCount(juszapConfiguration),
    [juszapConfiguration],
  );
}
